import { useSyncExternalStore } from 'react';
import { DEFAULT_SUPPORT_SETTINGS, PRESET_PROFILES } from '../data/supportProfiles';
import type { ProfileIconId, SupportProfile, SupportSettings } from '../types/supportProfiles';

type StoreState = {saved: SupportProfile[];activeId: string | null;settings: SupportSettings;};

const KEY = 'pf-support-profiles-v1';

function load(): StoreState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<StoreState>;
      return {
        saved: Array.isArray(parsed.saved) ? parsed.saved : [],
        activeId: parsed.activeId ?? null,
        settings: { ...DEFAULT_SUPPORT_SETTINGS, ...parsed.settings }
      };
    }
  } catch {
    // Storage unavailable — fall back to defaults.
  }
  return { saved: [], activeId: null, settings: DEFAULT_SUPPORT_SETTINGS };
}

let state = load();
const listeners = new Set<() => void>();

function setState(patch: Partial<StoreState>) {
  state = { ...state, ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // Ignore storage failures; state still lives for this session.
  }
  listeners.forEach((l) => l());
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

export function sameSettings(a: SupportSettings, b: SupportSettings) {
  return (Object.keys(a) as (keyof SupportSettings)[]).every((k) => {
    const av = a[k];
    const bv = b[k];
    return typeof av === 'number' && typeof bv === 'number' ? Math.abs(av - bv) < 1e-6 : av === bv;
  });
}

export function summarizeSettings(s: SupportSettings) {
  return `Touchpoint ${s.touchpointSize.toFixed(2)} mm · Density ${s.overhangDensity.toFixed(2)}`;
}

const applyProfile = (p: SupportProfile) => setState({ activeId: p.id, settings: { ...p.settings } });
const updateSettings = (patch: Partial<SupportSettings>) => setState({ settings: { ...state.settings, ...patch } });
const saveProfile = (name: string, icon: ProfileIconId) => {
  const profile: SupportProfile = {
    id: `user-${Date.now().toString(36)}`,
    name,
    icon,
    description: summarizeSettings(state.settings),
    settings: { ...state.settings }
  };
  setState({ saved: [...state.saved, profile], activeId: profile.id });
};
const replaceSaved = (saved: SupportProfile[]) => setState({ saved });

/** Support profile presets, user-saved profiles and the live auto-support settings. */
export function useSupportProfiles() {
  const s = useSyncExternalStore(subscribe, () => state);
  const all = [...PRESET_PROFILES, ...s.saved];
  const match = all.find((p) => p.id === s.activeId) ?? null;
  const isCustom = !match || !sameSettings(match.settings, s.settings);
  return {
    settings: s.settings,
    saved: s.saved,
    presets: PRESET_PROFILES,
    active: isCustom ? null : match,
    isCustom,
    applyProfile,
    updateSettings,
    saveProfile,
    replaceSaved
  };
}
